import _ from 'lodash';
import moment from 'moment';

import Assignee from './assignee';
import ConversationItem from './conversation_item';
import createModel, { prop } from './lib/create_model';
import CustomerProfile from './customer_profile';
import PhoneCall, { TransferStatus } from './phone_call';

export default createModel({
  modelName: 'ActiveCall',

  properties: {
    conversationId: prop(String),
    conversationItem: prop(ConversationItem),
    customer: prop(CustomerProfile),
    hasLivePhoneConnection: prop(Boolean).default(true),
    hasAgentClickedAcceptCall: prop(Boolean).default(false),
    initiator: prop(Assignee),
    routingGroupId: prop(String),
    transferee: prop(Assignee),
    searchResultId: prop(String),
    shouldSkipMatch: prop(Boolean),
  },

  didWarmTransferFail() {
    const phoneCall = this.conversationItem.content;
    if (
      phoneCall.transfer &&
      (phoneCall.transfer.status === TransferStatus.NOT_AVAILABLE || phoneCall.transfer.status === TransferStatus.ERROR)
    ) {
      return true;
    }

    if (!this.transferee || this.conversationItem.content.status === PhoneCall.Status.WARM_TRANSFER) {
      return false;
    }

    let transfereeParticipant = this.conversationItem.content.findParticipantById(this.transferee.agentId);
    return transfereeParticipant ? PhoneCall.isParticipantUnavailable(transfereeParticipant) : false;
  },

  isAgentOnLiveCall(agentId) {
    return this._isAgentOnCall(agentId) || this._isOutgoing() || this.hasAgentAcceptedCall();
  },

  _isAgentOnCall(agentId) {
    let agentParticipant = this.conversationItem.content.findParticipantById(agentId);
    return (
      this.conversationItem.content.isLive() &&
      !this.conversationItem.content.isWarmTransferRecipient(agentId) &&
      agentParticipant &&
      PhoneCall.isActiveParticipant(agentParticipant)
    );
  },

  hasCallOwnership(agentId) {
    // assumes max of 2 active agents on a call at once
    let activeAgentParticipants = this.conversationItem.content.getActiveAgentParticipants();
    if (activeAgentParticipants.length === 1) {
      return true;
    }
    let participantEvents = _.groupBy(this.conversationItem.content.participantEvents, event => event.participantId);
    let currentAgentDialingEvent = _.findLast(
      participantEvents[agentId],
      event => event.participantStatus === PhoneCall.ParticipantStatus.DIALING
    );
    let otherActiveAgent = _.find(activeAgentParticipants, participant => participant.participantId !== agentId);
    let otherAgentDialingEvent = _.findLast(
      participantEvents[otherActiveAgent.participantId],
      event => event.participantStatus === PhoneCall.ParticipantStatus.DIALING
    );
    return moment(currentAgentDialingEvent.eventTime).isBefore(otherAgentDialingEvent.eventTime);
  },

  isTransferLive() {
    return this.conversationItem.content.isTransferLive();
  },

  _isOutgoing() {
    return this.conversationItem.content.isOutgoing();
  },

  hasAgentAcceptedCall() {
    return this.hasAgentClickedAcceptCall;
  },

  setAgentHasAcceptedCall() {
    this.hasAgentClickedAcceptCall = true;
  },

  updateCustomer(customer) {
    this.customer = customer;
  },

  updateConversationId(conversationId) {
    this.conversationId = conversationId;
  },

  updateConversationItem(conversationItem) {
    this.conversationItem = conversationItem;
  },

  shouldDisplayIncomingCallNotification(agentId) {
    return (
      this.hasLivePhoneConnection &&
      !this.conversationItem.content.isOutgoing() &&
      this.conversationItem.content.isDialing(agentId) &&
      !this.hasAgentAcceptedCall()
    );
  },

  setTransferee(transferee) {
    this.transferee = transferee;
  },

  clearTransferee() {
    this.transferee = null;
  },

  setInitiator(initiator) {
    this.initiator = initiator;
  },

  setHasLiveConnection(isLive) {
    this.hasLivePhoneConnection = isLive;
  },

  hasLiveConnection() {
    return this.hasLivePhoneConnection;
  },

  setSearchResultId(resultId) {
    this.searchResultId = resultId;
  },

  setShouldSkipMatch(shouldSkipMatch) {
    this.shouldSkipMatch = shouldSkipMatch;
  },

  statics: {
    create(attrs) {
      return new this(attrs);
    },
  },
});



// WEBPACK FOOTER //
// ./src/scripts/domain/models/active_call.js